import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/workflows/user-workflows/{userWorkflowId}/petition-preselection';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['params'] & Partial<THIS_API['query']>;
type Output = THIS_API['response'];

/**
 * SWR hook for fetching the petition document pre-selection for a user workflow.
 * Returns the list of compiled document IDs that should be pre-selected in the
 * "Prepare Petition for Submission" modal, driven by the district template.
 *
 * When `skeleton` is true, the backend returns IDs for documents marked as
 * "required in skeleton" instead of the standard petition-selected documents.
 *
 * Returns `null` when no district template is assigned/inferred or when the
 * template has no petition selection data for the given chapter/joint, signaling
 * that the frontend should fall back to broad pre-selection.
 */
export const usePetitionPreselection = (
  params: Input | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const userWorkflowId = params?.userWorkflowId;
  const skeleton = params?.skeleton;

  const key = useMemo((): SwrKey => {
    if (!userWorkflowId) return null;
    const basePath = buildPath<THIS_API>(PATH_TEMPLATE, { userWorkflowId });
    if (skeleton) return `${basePath}?skeleton=true`;
    return basePath;
  }, [userWorkflowId, skeleton]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};
